import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import BidRow from './BidRow';
import SortControl from '../SortControl';
import Constants from '../../utils/Constants';
import NumberSteps from '../fields/NumberSteps';
import { FINISHED } from '../../utils/AuctionPhase';
import {
    isPriceDecrementModificationForbidden,
    isPriceIncrementModificationForbidden,
    roundToDecimals,
} from '../../utils';
import {
    auctionType as auctionTypeRef,
    bidModificationsType,
    bidsType,
    membersType,
    uiPhaseType,
} from '../../types';
import './_styles_/BidsList.css';
import BidOverview from './BidOverview';
import {
    ENERGY,
    getProductType,
} from '../../utils/ProductTypes';
import {
    getBiddingType,
    OPEN,
    SLOTTED,
} from '../../utils/BiddingTypes';
import {
    BUY,
    getAuctionType,
} from '../../utils/AuctionTypes';
import { compareBids } from '../../utils/bids';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';

BidsList.propTypes = {
    auction: auctionTypeRef.isRequired,
    auctionMembers: membersType.isRequired,
    bids: bidsType.isRequired,
    uiPhase: uiPhaseType.isRequired,
    isEditable: PropTypes.bool.isRequired,
    isQuantityStepValid: PropTypes.bool.isRequired,
    quantityStep: PropTypes.number,
    isPriceStepValid: PropTypes.bool.isRequired,
    priceStep: PropTypes.number.isRequired,
    onBidsSelected: PropTypes.func.isRequired,
    selectedBidIds: PropTypes.arrayOf(PropTypes.number),
    onBidModifications: PropTypes.func.isRequired,
    bidModifications: bidModificationsType,
};

export const groupIncEvaluator = (maxSize, step) => (oldValue) => {
    const newValue = roundToDecimals(oldValue + step);
    return maxSize !== null && newValue > maxSize ? maxSize : newValue;
};

export const groupDecEvaluator = (minSize, step) => (oldValue) => {
    const newValue = roundToDecimals(oldValue - step);
    return minSize !== null && newValue < minSize ? minSize : newValue;
};

export const filterBidsForMember = (bids, member) => (member === null
    ? bids.slice()
    : bids.filter((bid) => bid.member.memberId === member.memberId));

function BidsList(props) { // NOSONAR javascript:S3776
    const {
        auction,
        auctionMembers,
        bids,
        uiPhase,
        isEditable,
        isQuantityStepValid,
        quantityStep = null,
        isPriceStepValid,
        priceStep,
        onBidsSelected,
        selectedBidIds = [],
        bidModifications = [],
        onBidModifications,
    } = props;
    const { t } = useTranslation();
    const productType = getProductType(auction.product.productType);
    const biddingType = getBiddingType(auction.biddingType);
    const auctionType = getAuctionType(auction.auctionType);
    const [automaticallyCancelledBids, visibleBids] = useMemo(() => {
        let result;
        if (biddingType.is(SLOTTED)) {
            result = [[], bids];
        } else {
            result = _.partition(bids, (bid) => bid.cancelled);
        }
        return result;
    }, [bids, biddingType]);
    const [isAllBidsSelected, setAllBidsSelected] = useState(false);
    const distinctMembers = useMemo(() => {
        const members = bids.map((bid) => bid.member);
        const result = [];
        members.forEach((member) => {
            const alreadyIn = result.some((distinctMember) => distinctMember.memberId === member.memberId);
            if (!alreadyIn) {
                result.push(member);
            }
        });
        return result;
    }, [bids]);
    const onSelectedMemberIdChange = useCallback((newMemberId) => {
        setAllBidsSelected(false);
        setSelectedMember(
            newMemberId
                ? distinctMembers.find((distingMember) => distingMember.memberId === newMemberId)
                : null,
        );
        onBidsSelected([]);
        onBidModifications([]);
    }, [distinctMembers, onBidsSelected, onBidModifications]);
    const [sortingParam, setSortingParam] = useState({
        key: 'price',
        direction: auctionType.is(BUY) ? Constants.SORT_DESC : Constants.SORT_ASC,
    });
    const [selectedMember, setSelectedMember] = useState(distinctMembers.length === 1 && auctionMembers.length === 1 ? distinctMembers[0] : null);
    useEffectOnUpdate(() => {
        if (selectedMember && !distinctMembers.find((distingMember) => distingMember.memberId === selectedMember.memberId)) {
            onSelectedMemberIdChange(null);
        }
    }, [distinctMembers, selectedMember, onSelectedMemberIdChange]);
    const bidsForMember = filterBidsForMember(visibleBids, selectedMember).sort((bid1, bid2) => compareBids(bid1, bid2, sortingParam, auctionType, t));
    const automaticallyCancelledBidsForMember = filterBidsForMember(automaticallyCancelledBids, selectedMember);
    const isFinished = uiPhase.is(FINISHED);
    const selectAllBidsHandler = (isSelectAllBids) => {
        let newSelectedBids = [];
        if (isSelectAllBids) {
            newSelectedBids = bidsForMember
                .filter((bid) => !bid.obfuscated && !bid.cancelled)
                .map((bid) => bid.id);
        }
        setAllBidsSelected(isSelectAllBids);
        onBidsSelected(newSelectedBids);
    };
    const selectBidHandler = (bidId, isBidSelected) => {
        let newSelectedBidIds = [...selectedBidIds];
        if (isBidSelected) {
            if (selectedBidIds.indexOf(bidId) === -1) {
                newSelectedBidIds.push(bidId);
            }
        } else if (selectedBidIds.indexOf(bidId) !== -1) {
            newSelectedBidIds = selectedBidIds.filter((selectedBidId) => selectedBidId !== bidId);
        }
        onBidsSelected(newSelectedBidIds);
    };
    const prepareNewBidModification = (bidId, fieldName, newValue, isValid, oldBidModifications) => {
        const newBidModifications = [...oldBidModifications];
        const existingBidModification = newBidModifications.find((bidModification) => bidModification.id === bidId);
        const affectedBid = visibleBids.find((bid) => bid.id === bidId);
        const isValueModified = affectedBid[fieldName] !== newValue;
        if (existingBidModification) {
            if (isValueModified) {
                existingBidModification[fieldName] = newValue;
                existingBidModification.isValid = isValid;
            } else {
                delete existingBidModification[fieldName];
            }
        } else if (isValueModified) {
            newBidModifications.push({
                id: bidId,
                [fieldName]: newValue,
                isValid,
            });
        }
        return newBidModifications.filter((newBidModification) => Object.getOwnPropertyNames(newBidModification).length > 2);
    };
    const bidModificationHandler = (bidId, fieldName, newValue, isValid) => onBidModifications(prepareNewBidModification(bidId, fieldName, newValue, isValid, bidModifications));
    const onGroupModification = (fieldName, newValueEvaluator) => () => {
        const newBidModifications = selectedBidIds.reduce((prevModifications, selectedBidId) => {
            const affectedModification = prevModifications.find((bidModification) => bidModification.id === selectedBidId);
            let newValue;
            if (affectedModification && Object.prototype.hasOwnProperty.call(affectedModification, fieldName)) {
                newValue = newValueEvaluator(affectedModification[fieldName]);
            } else {
                const affectedBid = visibleBids.find((bid) => bid.id === selectedBidId);
                newValue = newValueEvaluator(affectedBid[fieldName]);
            }
            return prepareNewBidModification(selectedBidId, fieldName, newValue, true, prevModifications);
        }, [...bidModifications]);
        onBidModifications(newBidModifications);
    };
    const isModifySelectedQuantitiesDisabled = selectedBidIds.length === 0 || !isQuantityStepValid;
    const isModifySelectedPricesDisabled = selectedBidIds.length === 0 || !isPriceStepValid;
    const retrieveCurrentBidPrice = (forBid) => {
        const existingBidModification = bidModifications.find((bidModification) => bidModification.id === forBid.id);
        let result = forBid.price;
        if (existingBidModification && typeof existingBidModification.price !== 'undefined') {
            result = existingBidModification.price;
        }
        return result;
    };
    const isPriceIncrementDisabled = isModifySelectedPricesDisabled || (
        isPriceIncrementModificationForbidden(auction) && selectedBidIds.some((selectedBidId) => {
            const affectedBid = visibleBids.find((bid) => bid.id === selectedBidId);
            const currentBidPrice = retrieveCurrentBidPrice(affectedBid);
            return roundToDecimals(currentBidPrice + priceStep) > affectedBid.price;
        })
    );
    const isPriceDecrementDisabled = isModifySelectedPricesDisabled || (
        isPriceDecrementModificationForbidden(auction) && selectedBidIds.some((selectedBidId) => {
            const affectedBid = visibleBids.find((bid) => bid.id === selectedBidId);
            const currentBidPrice = retrieveCurrentBidPrice(affectedBid);
            return roundToDecimals(currentBidPrice - priceStep) < affectedBid.price;
        })
    );
    const bidOverview = (bidsForMember.length > 0 || automaticallyCancelledBidsForMember.length > 0) && (
        <BidOverview
            auction={auction}
            bidsForMember={bidsForMember}
            distinctMembers={distinctMembers}
            onSelectedMemberIdChange={onSelectedMemberIdChange}
            selectedMember={selectedMember}
            automaticallyCancelledBidsForMember={automaticallyCancelledBidsForMember}
        />
    );
    let quantityColumn = null;
    if (biddingType.is(OPEN)) {
        quantityColumn = (
            <th className="auction-bids--table--header-row--quantity">
                <div className="auction-bids--table--header-row--quantity--container">
                    {isEditable ? (
                        <NumberSteps
                            onIncrement={onGroupModification('quantity', groupIncEvaluator(auction.maxBidSize, quantityStep))}
                            onDecrement={onGroupModification('quantity', groupDecEvaluator(auction.smallestUnit, quantityStep))}
                            incrementDisabled={isModifySelectedQuantitiesDisabled}
                            decrementDisabled={isModifySelectedQuantitiesDisabled}
                            incrementTitle={`+${quantityStep}`}
                            decrementTitle={`-${quantityStep}`}
                        >
                            <SortControl sortingKey="quantity" sortingParam={sortingParam} onChange={setSortingParam} className="auction-bids--table--header-row--quantity--container--value">{t('auctions.bids-list.quantity')}</SortControl>
                        </NumberSteps>
                    ) : (
                        <SortControl sortingKey="quantity" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.quantity')}</SortControl>
                    )}
                </div>
            </th>
        );
    }
    let priceColumn;
    if (isEditable) {
        priceColumn = (
            <NumberSteps
                onIncrement={onGroupModification('price', groupIncEvaluator(auction.maxPrice, priceStep))}
                onDecrement={onGroupModification('price', groupDecEvaluator(auction.minPrice, priceStep))}
                incrementDisabled={isPriceIncrementDisabled}
                decrementDisabled={isPriceDecrementDisabled}
                incrementTitle={`+${priceStep}`}
                decrementTitle={`-${priceStep}`}
            >
                <SortControl sortingKey="price" sortingParam={sortingParam} onChange={setSortingParam} className="auction-bids--table--header-row--price--container--value">{t('auctions.bids-list.price')}</SortControl>
            </NumberSteps>
        );
    } else {
        priceColumn = (
            <SortControl sortingKey="price" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.price')}</SortControl>
        );
    }
    const bidTable = bidsForMember.length
        ? (
            <table className="list-table">
                <thead>
                    <tr>
                        {isEditable && (
                            <th className="auction-bids--table--header-row--checkbox" title={t('auctions.bids-list.select-all-bid-rows')}>
                                <input type="checkbox" className="auction-bids--table--header-row--checkbox--selector" aria-label={t('auctions.bids-list.select-all-bid-rows')} checked={isAllBidsSelected} onChange={(event) => selectAllBidsHandler(event.target.checked)} />
                            </th>
                        )}
                        <th className="auction-bids--table--header-row--user">
                            <SortControl sortingKey="user" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.user')}</SortControl>
                        </th>
                        <th className="auction-bids--table--header-row--modified-by">
                            <SortControl sortingKey="modifiedBy" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.modified-by')}</SortControl>
                        </th>
                        {productType.is(ENERGY) && (
                            <th className="auction-bids--table--header-row--balancing-group">
                                <SortControl sortingKey="balancingGroup" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.balancing-group')}</SortControl>
                            </th>
                        )}
                        <th className="auction-bids--table--header-row--member">
                            <SortControl sortingKey="member" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.member')}</SortControl>
                        </th>
                        {quantityColumn}
                        <th className="auction-bids--table--header-row--price">
                            <div className="auction-bids--table--header-row--price--container">
                                {priceColumn}
                            </div>
                        </th>
                        {auction.showRanking && (
                            <th className="auction-bids--table--header-row--rank">
                                <SortControl sortingKey="rank" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.rank')}</SortControl>
                            </th>
                        )}
                        {isFinished && biddingType.is(OPEN) && (
                            <>
                                <th className="auction-bids--table--header-row--allocated">
                                    <SortControl sortingKey="quantityAllocated" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.allocated')}</SortControl>
                                </th>
                                <th className="auction-bids--table--header-row--bid-value">
                                    <SortControl sortingKey="bidVolume" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.bid-value')}</SortControl>
                                </th>
                            </>
                        )}
                        {isFinished && biddingType.is(SLOTTED) && (
                            <th className="auction-bids--table--header-row--result">
                                <SortControl sortingKey="winner" sortingParam={sortingParam} onChange={setSortingParam}>{t('auctions.bids-list.result')}</SortControl>
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {bidsForMember.map((bid) => (
                        <BidRow
                            auction={auction}
                            bid={bid}
                            isSelected={selectedBidIds.indexOf(bid.id) !== -1}
                            onBidSelect={selectBidHandler}
                            onBidModification={bidModificationHandler}
                            bidModification={bidModifications.find((bidModification) => bidModification.id === bid.id)}
                            key={bid.id}
                            quantityStep={quantityStep}
                            priceStep={priceStep}
                            isQuantityStepValid={isQuantityStepValid}
                            isPriceStepValid={isPriceStepValid}
                            isEditable={isEditable}
                            isFinished={isFinished}
                        />
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td />
                    </tr>
                </tfoot>
            </table>
        )
        : (
            <div className="bids-no-items">{t('auctions.bids-list.no-items')}</div>
        );
    return (
        <div>
            {bidOverview}
            {bidTable}
        </div>
    );
}

export default BidsList;
